import React, { useMemo, useState } from 'react';
import type { TextInputProps } from 'react-native';

import { TypeaheadSearch, type TypeaheadOption } from '@/components/search/typeahead-search';

export interface LocalTypeaheadProps<T> {
	items: T[];
	toOption: (item: T) => TypeaheadOption;
	value: string | null | undefined;
	onChange: (id: string | null) => void;
	label?: string;
	placeholder?: string;
	maxResults?: number;
	style?: TextInputProps['style'];
	testID?: string;
	autoFocus?: boolean;
	/** When true, show a "Use: <text>" option for unmatched input so the
	 *  user can introduce a new label on the fly (e.g. a free-form charge
	 *  category not in the suggested list). */
	allowCreate?: boolean;
}

/**
 * In-memory typeahead for arrays already loaded on the client. Wraps the
 * shared `TypeaheadSearch` so callers don't reimplement filter/select state
 * for every dropdown. Use this anywhere a select would otherwise list more
 * than ~5 options (vendors, GL codes, accounts, etc.).
 */
export const LocalTypeahead = <T,>({
	items,
	toOption,
	value,
	onChange,
	label,
	placeholder,
	maxResults = 25,
	style,
	testID,
	autoFocus,
	allowCreate = false,
}: LocalTypeaheadProps<T>) => {
	const [query, setQuery] = useState('');

	const allOptions = useMemo(() => items.map(toOption), [items, toOption]);
	const selectedOption = useMemo<TypeaheadOption | null>(
		() => {
			if (!value) return null;
			const found = allOptions.find((o) => o.id === value);
			if (found) return found;
			// allowCreate: a value not in the option list is a custom one;
			// surface it to the field so the user sees their own label.
			if (allowCreate) return { id: value, label: value };
			return null;
		},
		[allOptions, value, allowCreate],
	);

	const filtered = useMemo(() => {
		const q = query.trim().toLowerCase();
		if (!q) return allOptions;
		return allOptions.filter((o) =>
			o.label.toLowerCase().includes(q) || (o.description ?? '').toLowerCase().includes(q),
		);
	}, [allOptions, query]);

	return (
		<TypeaheadSearch
			label={label}
			placeholder={placeholder}
			options={filtered}
			selectedOption={selectedOption}
			onSearch={setQuery}
			onSelect={(o) => {
				if (!o) { onChange(null); return; }
				// "Use: <text>" custom option emits id="custom:<text>" — we
				// strip the prefix so the parent gets the raw label.
				if (o.id.startsWith('custom:')) onChange(o.label);
				else onChange(o.id);
			}}
			allowCreate={allowCreate}
			maxResults={maxResults}
			style={style}
			testID={testID}
			autoFocus={autoFocus}
		/>
	);
};
